import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Progress, Tag, Typography, Spin, Modal, Result, Input } from "antd";
import {
  CloseOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { api } from "@/services/api";
import { API_ENDPOINTS } from "@/constants";
import type { ApiResponse, Card, Deck, Session } from "@/types";
import { useAuthStore } from "@/stores/authStore";
import { resolveMediaUrl } from "@/utils/url";
import { MarkdownKatex } from "@/components/import/MarkdownKatex";
import AiChatModal from "@/components/AiChatModal";

const { Title, Text } = Typography;

interface LocalInteraction {
  cardId: string;
  response: "UNDERSTOOD" | "NOT_UNDERSTOOD" | "SKIPPED";
  comfortScore?: number;
  responseTimeMs: number;
}

const COMFORT_LEVELS = [
  { score: 1, label: "Lost", color: "#E53935" },
  { score: 2, label: "Shaky", color: "#F59E0B" },
  { score: 3, label: "Okay", color: "#FDD835" },
  { score: 4, label: "Good", color: "#66BB6A" },
  { score: 5, label: "Nailed", color: "#43A047" },
];

const BATCH_SIZE = 3;
const MIN_READ_MS = 6000;
function getDeckProgressKey(deckId: string, userId: string) {
  return `cardwise-deck-progress-${userId}-${deckId}`;
}

function playCardSound(type: "understood" | "not_understood", userId: string | undefined) {
  try {
    const key = `cardwise-sound-enabled-${userId ?? 'guest'}`;
    const enabled = localStorage.getItem(key);
    if (enabled === "false") return;

    const ctx = new AudioContext();
    const playTone = (
      startAt: number,
      frequency: number,
      gainPeak: number,
      duration: number,
      oscType: OscillatorType = "sine",
    ) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = oscType;
      osc.frequency.setValueAtTime(frequency, startAt);
      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(gainPeak, startAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration);
      osc.start(startAt);
      osc.stop(startAt + duration);
    };

    const now = ctx.currentTime;
    if (type === "understood") {
      // Ascending ding: C5 → G5
      playTone(now, 523, 0.3, 0.4);
      playTone(now + 0.18, 784, 0.25, 0.5);
    } else {
      // Soft descending tone: G4 → D4
      playTone(now, 392, 0.2, 0.35, "triangle");
      playTone(now + 0.25, 294, 0.15, 0.45, "triangle");
    }

    // Close context after sounds finish to free resources
    setTimeout(() => ctx.close(), 1000);
  } catch {
    // Web Audio API not available — silently skip
  }
}

export default function CardReviewPage() {
  const { deckId } = useParams<{ deckId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isViewOnly = searchParams.get("mode") === "view";

  const { user } = useAuthStore();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(() => {
    if (!deckId) return 0;
    const userId = useAuthStore.getState().user?.id ?? "";
    const saved = localStorage.getItem(getDeckProgressKey(deckId, userId));
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [interactions, setInteractions] = useState<LocalInteraction[]>([]);
  const [cardStartTime, setCardStartTime] = useState<number>(Date.now());
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [canRespond, setCanRespond] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [helpNote, setHelpNote] = useState("");
  const [showHint, setShowHint] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiChatKey, setAiChatKey] = useState(0);

  const pendingBatchRef = useRef<LocalInteraction[]>([]);
  const failedBatchRef = useRef<LocalInteraction[]>([]);
  const sessionStartedRef = useRef<string | null>(null);
  const skippedCountRef = useRef(0);

  // Fetch deck with cards
  const queryClient = useQueryClient();
  const {
    data: deckData,
    isLoading: isDeckLoading,
    error: deckError,
  } = useQuery({
    queryKey: ["deck", deckId],
    queryFn: () =>
      api.get<never, ApiResponse<Deck>>(`${API_ENDPOINTS.DECKS}/${deckId}`),
    enabled: !!deckId,
  });

  const deck = deckData?.data;
  const cards: Card[] = (deck?.cards ?? []).sort(
    (a, b) => a.sequenceOrder - b.sequenceOrder,
  );

  // Start or resume session
  const startSessionMutation = useMutation({
    mutationFn: () => {
      console.log("Mutation started, deckId:", deckId);
      return api.post<never, ApiResponse<Session>>(API_ENDPOINTS.SESSIONS, {
        deckId,
      });
    },
    onSuccess: (res) => {
      console.log("Session created response:", res);
      console.log("Session data:", res.data);
      // res is already the response body due to axios interceptor
      // It has structure: { success: true, data: Session }
      const session = res.data;
      console.log("Setting sessionId:", session?.id);
      if (session?.id) {
        setSessionId(session.id);
      } else {
        console.error("No session ID found in response");
      }

      // Resume from where the student left off
      if (session.cardsReviewed > 0 && session.cardsReviewed < cards.length) {
        console.log("Resuming from card index:", session.cardsReviewed);
        setCurrentCardIndex(session.cardsReviewed);
      }
    },
    onError: (error) => {
      console.error("Failed to start session:", error);
    },
  });

  // Complete session mutation
  const completeSessionMutation = useMutation({
    mutationFn: () => {
      if (!sessionId) throw new Error("No session ID");
      return api.post<never, ApiResponse<Session>>(
        `${API_ENDPOINTS.SESSIONS}/${sessionId}/complete`,
        { comment: undefined },
      );
    },
    onSuccess: () => {
      console.log("Session completed successfully");
      navigate(`/student/sessions/${sessionId}/complete`, { replace: true, state: { skippedCount: skippedCountRef.current } });
    },
    onError: (error) => {
      console.error("Failed to complete session:", error);
      navigate(`/student/sessions/${sessionId}/complete`, { replace: true, state: { skippedCount: skippedCountRef.current } });
    },
  });

  // Submit help request
  const submitHelpMutation = useMutation({
    mutationFn: (vars: { note: string; cardId: string; deckId: string; sessionId: string }) =>
      api.post(API_ENDPOINTS.HELP_REQUESTS, {
        cardId: vars.cardId,
        deckId: vars.deckId,
        sessionId: vars.sessionId,
        note: vars.note.trim() || undefined,
      }),
    onSuccess: () => {
      setHelpModalOpen(false);
      setHelpNote("");
      handleResponse("NOT_UNDERSTOOD");
    },
    onError: () => {
      // Still record the interaction even if help request fails
      setHelpModalOpen(false);
      setHelpNote("");
      handleResponse("NOT_UNDERSTOOD");
    },
  });

  // Reset session when component mounts with a deck
  useEffect(() => {
    if (deckId) {
      console.log(
        "Component mounted/deckId changed, resetting ref for deck:",
        deckId,
      );
      // Reset the ref when entering this deck to allow re-attempts
      sessionStartedRef.current = null;
      setSessionId(null);
    }

    // Cleanup: invalidate deck cache and student decks list when leaving
    return () => {
      if (deckId) {
        queryClient.invalidateQueries({ queryKey: ["deck", deckId] });
        queryClient.invalidateQueries({ queryKey: ["student", "decks"] });
      }
    };
  }, [deckId, queryClient]);

  // Start session once deck is loaded
  useEffect(() => {
    console.log("Session effect:", {
      deckId: deck?.id,
      cardsLength: cards.length,
      sessionStartedForDeck: sessionStartedRef.current,
    });
    // Create session if we have a deck with cards and haven't created a session yet for this visit
    if (deck && cards.length > 0 && !sessionStartedRef.current && !isViewOnly) {
      console.log("Creating session for deck:", deck.id);
      sessionStartedRef.current = deck.id;
      startSessionMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck?.id, cards.length]);

  // Reset card start time when card changes
  useEffect(() => {
    setCardStartTime(Date.now());
  }, [currentCardIndex]);

  // Enforce minimum read time per card — only after loading is done (session ready)
  useEffect(() => {
    if (isViewOnly || !sessionId) return;
    setCanRespond(false);
    setReadProgress(0);
    // Small delay so the browser paints width=0% before the fill starts animating
    const startFill = setTimeout(() => setReadProgress(100), 50);
    const enableBtn = setTimeout(() => setCanRespond(true), MIN_READ_MS);
    return () => {
      clearTimeout(startFill);
      clearTimeout(enableBtn);
    };
  }, [currentCardIndex, sessionId, isViewOnly]);

  // Send batch of interactions, retrying any previously failed ones
  const sendBatch = useCallback(
    async (batch: LocalInteraction[], isLastBatch = false) => {
      // Check if sessionId is available - if not, we can't send yet
      if (!sessionId) {
        console.warn(
          `[CardReview] Cannot send batch - sessionId not available yet. Storing ${batch.length} interactions for retry.`,
        );
        failedBatchRef.current = [...failedBatchRef.current, ...batch];
        return false;
      }

      if (batch.length === 0) return true;

      // Include any previously failed interactions
      const allToSend = [...failedBatchRef.current, ...batch];
      failedBatchRef.current = [];

      try {
        console.log(
          `[CardReview] Sending ${allToSend.length} interactions to session ${sessionId}`,
          allToSend,
        );
        await api.post(`${API_ENDPOINTS.SESSIONS}/${sessionId}/interactions`, {
          interactions: allToSend,
        });
        console.log(
          `[CardReview] Successfully sent ${allToSend.length} interactions`,
        );
        return true;
      } catch (error) {
        console.error(`[CardReview] Failed to send interactions:`, error);

        if (isLastBatch) {
          // On last card, retry once more
          try {
            console.log(
              `[CardReview] Retrying ${allToSend.length} interactions...`,
            );
            await api.post(
              `${API_ENDPOINTS.SESSIONS}/${sessionId}/interactions`,
              { interactions: allToSend },
            );
            console.log(`[CardReview] Retry successful`);
            return true;
          } catch (retryError) {
            console.error(`[CardReview] Retry failed:`, retryError);
            // Still proceed but log warning - interactions may be incomplete
            failedBatchRef.current = allToSend;
            return false;
          }
        } else {
          // Store failed batch for retry with next batch
          console.log(`[CardReview] Storing failed batch for retry`);
          failedBatchRef.current = allToSend;
          return false;
        }
      }
    },
    [sessionId],
  );

  // Send any pending/failed interactions once sessionId is available
  useEffect(() => {
    const sendPendingInteractions = async () => {
      if (
        !sessionId ||
        (failedBatchRef.current.length === 0 &&
          pendingBatchRef.current.length === 0)
      ) {
        return;
      }

      const allPending = [
        ...pendingBatchRef.current,
        ...failedBatchRef.current,
      ];
      if (allPending.length === 0) return;

      console.log(
        `[CardReview] Session available, sending ${allPending.length} pending interactions`,
      );
      pendingBatchRef.current = [];
      failedBatchRef.current = [];
      await sendBatch(allPending, false);
    };

    sendPendingInteractions();
  }, [sessionId, sendBatch]);

  // Handle card response (internal, supports optional comfort score)
  const handleResponseInternal = useCallback(
    async (response: "UNDERSTOOD" | "NOT_UNDERSTOOD", comfortScore?: number) => {
      if (!sessionId || isSubmitting || cards.length === 0) return;

      const currentCard = cards[currentCardIndex];
      if (!currentCard) return;

      const interaction: LocalInteraction = {
        cardId: currentCard.id,
        response,
        comfortScore,
        responseTimeMs: Date.now() - cardStartTime,
      };

      playCardSound(
        response === "UNDERSTOOD" ? "understood" : "not_understood",
        user?.id,
      );

      const newInteractions = [...interactions, interaction];
      setInteractions(newInteractions);
      pendingBatchRef.current = [...pendingBatchRef.current, interaction];

      const isLastCard = currentCardIndex >= cards.length - 1;

      console.log(
        `[CardReview] Card ${currentCardIndex + 1}/${cards.length}: ${response}`,
        {
          sessionId,
          cardId: currentCard.id,
          isLastCard,
          pendingCount: pendingBatchRef.current.length,
        },
      );

      // Send batch if threshold reached or last card
      if (pendingBatchRef.current.length >= BATCH_SIZE || isLastCard) {
        const batch = [...pendingBatchRef.current];
        pendingBatchRef.current = [];
        const success = await sendBatch(batch, isLastCard);
        console.log(
          `[CardReview] Batch send ${success ? "successful" : "failed"} (sessionId: ${sessionId})`,
        );

        // If this is the last card, wait for pending interactions to be sent before navigating
        if (isLastCard && !success && !sessionId) {
          console.log(
            `[CardReview] Last card batch failed - sessionId not available yet, will retry via effect`,
          );
          // The useEffect will handle retrying when sessionId becomes available
          // Wait a moment to give the effect time to execute
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      if (isLastCard) {
        if (deckId) localStorage.removeItem(getDeckProgressKey(deckId, user?.id ?? ""));
        setIsSubmitting(true);
        console.log("Last card reached, completing session");
        // Complete session first, then navigate
        completeSessionMutation.mutate();
      } else {
        const nextIndex = currentCardIndex + 1;
        if (deckId)
          localStorage.setItem(getDeckProgressKey(deckId, user?.id ?? ""), String(nextIndex));
        setCurrentCardIndex(nextIndex);
        setIsFlipped(false);
        setShowHint(false);
        setAiChatKey((k) => k + 1);
      }
    },
    [
      sessionId,
      isSubmitting,
      cards,
      currentCardIndex,
      cardStartTime,
      interactions,
      sendBatch,
      navigate,
      completeSessionMutation,
      deckId,
    ],
  );

  // Handle comfort-based response (wraps handleResponseInternal)
  const handleComfortResponse = useCallback(
    (score: number) => {
      const response: "UNDERSTOOD" | "NOT_UNDERSTOOD" =
        score >= 3 ? "UNDERSTOOD" : "NOT_UNDERSTOOD";
      handleResponseInternal(response, score);
    },
    [handleResponseInternal],
  );

  // Handle legacy binary response (wraps handleResponseInternal)
  const handleResponse = useCallback(
    (response: "UNDERSTOOD" | "NOT_UNDERSTOOD") => {
      handleResponseInternal(response, undefined);
    },
    [handleResponseInternal],
  );

  // Handle skip — records SKIPPED response and advances without comfort score
  const handleSkip = useCallback(async () => {
    if (!sessionId || isSubmitting || cards.length === 0) return;
    const currentCard = cards[currentCardIndex];
    if (!currentCard) return;

    const interaction: LocalInteraction = {
      cardId: currentCard.id,
      response: "SKIPPED",
      responseTimeMs: Date.now() - cardStartTime,
    };

    skippedCountRef.current += 1;

    const newInteractions = [...interactions, interaction];
    setInteractions(newInteractions);
    pendingBatchRef.current = [...pendingBatchRef.current, interaction];

    const isLastCard = currentCardIndex >= cards.length - 1;

    if (pendingBatchRef.current.length >= BATCH_SIZE || isLastCard) {
      const batch = [...pendingBatchRef.current];
      pendingBatchRef.current = [];
      await sendBatch(batch, isLastCard);
    }

    if (isLastCard) {
      if (deckId) localStorage.removeItem(getDeckProgressKey(deckId, user?.id ?? ""));
      setIsSubmitting(true);
      completeSessionMutation.mutate();
    } else {
      const nextIndex = currentCardIndex + 1;
      if (deckId) localStorage.setItem(getDeckProgressKey(deckId, user?.id ?? ""), String(nextIndex));
      setCurrentCardIndex(nextIndex);
      setIsFlipped(false);
      setShowHint(false);
      setAiChatKey((k) => k + 1);
    }
  }, [
    sessionId,
    isSubmitting,
    cards,
    currentCardIndex,
    cardStartTime,
    interactions,
    sendBatch,
    completeSessionMutation,
    deckId,
    user?.id,
  ]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if a textarea/input is focused
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      switch (e.key) {
        case "1":
        case "2":
        case "3":
        case "4":
        case "5":
          if (!canRespond) break;
          e.preventDefault();
          handleComfortResponse(parseInt(e.key, 10));
          break;
        case "ArrowRight":
        case "y":
        case "Y":
          if (!canRespond) break;
          e.preventDefault();
          handleComfortResponse(5);
          break;
        case "ArrowLeft":
        case "n":
        case "N":
          if (!canRespond) break;
          e.preventDefault();
          handleComfortResponse(1);
          break;
        case "s":
        case "S":
          if (!canRespond) break;
          e.preventDefault();
          handleSkip();
          break;
        case "Escape":
          e.preventDefault();
          setShowExitConfirm(true);
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleComfortResponse, handleSkip, canRespond]);

  const handleExit = () => {
    setShowExitConfirm(false);
    navigate("/student/decks");
  };

  // Loading states
  if (isDeckLoading || (!isViewOnly && startSessionMutation.isPending)) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#faf8f5",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        <Spin size="large" />
        <Text style={{ marginTop: 16, fontSize: 16, color: "#64748b" }}>
          Getting your cards ready...
        </Text>
      </div>
    );
  }

  // Error state
  if (
    deckError ||
    startSessionMutation.isError ||
    !deck ||
    cards.length === 0
  ) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#faf8f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        <Result
          status="error"
          title="Oops! Something went wrong"
          subTitle={
            cards.length === 0 && deck
              ? "This deck has no cards yet."
              : "Could not load the deck. Please try again."
          }
          extra={
            <Button
              type="primary"
              size="large"
              onClick={() => navigate("/student")}
            >
              Back to Home
            </Button>
          }
        />
      </div>
    );
  }

  const currentCard = cards[currentCardIndex];
  const progressPercent = ((currentCardIndex + 1) / cards.length) * 100;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#faf8f5",
        display: "flex",
        flexDirection: "column",
        zIndex: 1000,
        overflow: "auto",
      }}
    >
      {/* Top bar: progress + close */}
      <div
        style={{
          padding: "16px 24px 0",
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 6,
            }}
          >
            <Text strong style={{ fontSize: 15, color: "#475569" }}>
              Card {currentCardIndex + 1} of {cards.length}
            </Text>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Text style={{ fontSize: 14, color: "#94a3b8" }}>{deck.topicName}</Text>
              {isViewOnly && (
                <span style={{ fontSize: 11, fontWeight: 600, color: "#6366f1", background: "#ede9fe", borderRadius: 6, padding: "2px 8px" }}>
                  View Only
                </span>
              )}
            </div>
          </div>
          <Progress
            percent={progressPercent}
            showInfo={false}
            strokeColor={{
              "0%": "#6366f1",
              "100%": "#22c55e",
            }}
            trailColor="#e2e8f0"
            size={["100%", 8]}
          />
        </div>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={() => setShowExitConfirm(true)}
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#94a3b8",
            flexShrink: 0,
          }}
          aria-label="Exit review"
        />
      </div>

      {/* Card content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px 24px 16px",
          overflow: "auto",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: 16,
            padding: "40px 32px",
            maxWidth: 640,
            width: "100%",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            textAlign: "center",
          }}
        >
          {currentCard?.title && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: currentCard.frontText ? 16 : 0 }}>
              <Title
                level={3}
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#1e293b",
                }}
              >
                {currentCard.title}
              </Title>
              {currentCard.difficulty && (
                <Tag color={currentCard.difficulty === "EASY" ? "green" : currentCard.difficulty === "MEDIUM" ? "orange" : "purple"}>
                  {currentCard.difficulty}
                </Tag>
              )}
            </div>
          )}

          {currentCard?.imageUrl && (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                marginBottom: 20,
              }}
            >
              <img
                src={resolveMediaUrl(currentCard.imageUrl)}
                alt={currentCard.title ?? "Card image"}
                style={{
                  maxWidth: "100%",
                  maxHeight: 240,
                  objectFit: "contain",
                  display: "block",
                  borderRadius: 12,
                }}
              />
            </div>
          )}

          <div
            style={{
              fontSize: 18,
              lineHeight: 1.7,
              color: "#334155",
              textAlign: "left",
            }}
          >
            <MarkdownKatex
              content={
                isFlipped && currentCard?.backText
                  ? currentCard.backText
                  : (currentCard?.frontText ?? "")
              }
            />
          </div>

          {/* Hint toggle — shown on front side only */}
          {!isFlipped && currentCard?.hint && (
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <Button
                size="small"
                type="dashed"
                onClick={() => setShowHint((h) => !h)}
                style={{ fontSize: 13, color: "#64748b" }}
              >
                {showHint ? "Hide Hint" : "Show Hint"}
              </Button>
              {showHint && (
                <div style={{ marginTop: 8, padding: 12, background: "#fffbe6", borderRadius: 8, textAlign: "left" }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: "#d48806", marginBottom: 4 }}>Hint</div>
                  <div style={{ fontSize: 15, color: "#334155" }}>{currentCard.hint}</div>
                </div>
              )}
            </div>
          )}

          {/* Rich fields — shown on back side */}
          {isFlipped && (
            <>
              {currentCard?.explanation && (
                <div style={{ marginTop: 12, padding: 12, background: "#f5f5f5", borderRadius: 8, textAlign: "left" }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: "#666", marginBottom: 4 }}>Why?</div>
                  <div style={{ fontSize: 15, color: "#334155" }}>{currentCard.explanation}</div>
                </div>
              )}
              {currentCard?.example && (
                <div style={{ marginTop: 8, padding: 12, background: "#f0f0f0", borderRadius: 8, textAlign: "left" }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: "#666", marginBottom: 4 }}>Example</div>
                  <div style={{ fontSize: 15, color: "#334155" }}>{currentCard.example}</div>
                </div>
              )}
              {currentCard?.commonMistake && (
                <div style={{ marginTop: 8, padding: 12, background: "rgba(229, 57, 53, 0.08)", borderRadius: 8, textAlign: "left" }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: "#E53935", marginBottom: 4 }}>Common Mistake</div>
                  <div style={{ fontSize: 15, color: "#334155" }}>{currentCard.commonMistake}</div>
                </div>
              )}
              {currentCard?.selfCheck && (
                <div style={{ marginTop: 8, padding: 12, background: "rgba(21, 101, 192, 0.08)", borderRadius: 8, textAlign: "left" }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: "#1565C0", marginBottom: 4 }}>Self-Check</div>
                  <div style={{ fontSize: 15, color: "#334155" }}>{currentCard.selfCheck}</div>
                </div>
              )}
            </>
          )}

          {currentCard?.backText && (
            <button
              onClick={() => { setIsFlipped((f) => !f); setShowHint(false); }}
              style={{
                marginTop: 20,
                padding: "8px 20px",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                background: isFlipped ? "#f1f5f9" : "#fff",
                cursor: "pointer",
                fontSize: 14,
                color: "#64748b",
              }}
            >
              {isFlipped ? "Show Question" : "Show Answer"}
            </button>
          )}
        </div>
      </div>

      {/* Read timer fill bar — hidden in view-only mode */}
      {!isViewOnly && (
        <div style={{ padding: "0 24px 8px", flexShrink: 0 }}>
          <div style={{ height: 5, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${readProgress}%`,
                background: canRespond ? "#22c55e" : "#6366f1",
                borderRadius: 3,
                transition: readProgress === 0 ? "none" : `width ${MIN_READ_MS}ms linear`,
              }}
            />
          </div>
          {!canRespond && (
            <div style={{ textAlign: "center", marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Read the card first…</Text>
            </div>
          )}
        </div>
      )}

      <div style={{ padding: "8px 16px 32px", display: "flex", justifyContent: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
        {isViewOnly ? (
          <>
            <Button
              size="large"
              onClick={() => setCurrentCardIndex((i) => Math.max(0, i - 1))}
              disabled={currentCardIndex === 0}
              style={{ minHeight: 56, flex: 1, maxWidth: 200, borderRadius: 14, fontSize: 16, fontWeight: 600 }}
            >
              ← Previous
            </Button>
            {currentCardIndex < cards.length - 1 ? (
              <Button
                size="large"
                type="primary"
                onClick={() => { setCurrentCardIndex((i) => i + 1); setIsFlipped(false); setShowHint(false); }}
                style={{ minHeight: 56, flex: 1, maxWidth: 200, borderRadius: 14, fontSize: 16, fontWeight: 600 }}
              >
                Next →
              </Button>
            ) : (
              <Button
                size="large"
                type="primary"
                onClick={() => navigate("/student/decks")}
                style={{ minHeight: 56, flex: 1, maxWidth: 200, borderRadius: 14, fontSize: 16, fontWeight: 600, background: "#6366f1", borderColor: "#6366f1" }}
              >
                Done
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              size="large"
              onClick={() => setAiChatOpen(true)}
              disabled={isSubmitting || !canRespond}
              style={{
                minHeight: 56, flex: 1, minWidth: 80, maxWidth: 120, borderRadius: 14, fontSize: 15, fontWeight: 600,
                color: "#6366f1", borderColor: "#c7d2fe", background: "#ede9fe",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
              icon={<RobotOutlined style={{ fontSize: 16 }} />}
              aria-label="Ask AI tutor for help"
            >
              Ask AI
            </Button>
            <Button
              size="large"
              onClick={() => setHelpModalOpen(true)}
              disabled={isSubmitting || !canRespond}
              style={{
                minHeight: 56, flex: 1, minWidth: 80, maxWidth: 120, borderRadius: 14, fontSize: 15, fontWeight: 600,
                color: "#dc2626", borderColor: "#fecaca", background: "#fef2f2",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
              icon={<QuestionCircleOutlined style={{ fontSize: 16 }} />}
            >
              Help
            </Button>
            <Button
              size="large"
              onClick={handleSkip}
              disabled={isSubmitting || !canRespond}
              style={{
                minHeight: 56, flex: 1, minWidth: 80, maxWidth: 120, borderRadius: 14, fontSize: 15, fontWeight: 600,
                color: "#64748b", borderColor: "#cbd5e1", background: "#f8fafc",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              Skip
            </Button>
            {COMFORT_LEVELS.map((level) => (
              <Button
                key={level.score}
                size="large"
                onClick={() => handleComfortResponse(level.score)}
                disabled={isSubmitting || !canRespond}
                style={{
                  minHeight: 56, flex: 1, minWidth: 80, maxWidth: 120, borderRadius: 14, fontSize: 15, fontWeight: 600,
                  color: level.color, borderColor: level.color, background: `${level.color}33`,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <span>{level.score}</span>
                <span className="comfort-label">{level.label}</span>
              </Button>
            ))}
          </>
        )}
      </div>

      {/* Keyboard hint — hidden in view-only and on touch devices */}
      {!isViewOnly && (
        <div style={{ textAlign: "center", paddingBottom: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }} className="keyboard-hint-desktop">
            Keyboard: 1-5 = Comfort Level &middot; S = Skip &middot; Esc = Exit
          </Text>
        </div>
      )}

      {/* Help request modal */}
      <Modal
        open={helpModalOpen}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <QuestionCircleOutlined style={{ color: "#f59e0b", fontSize: 18 }} />
            <span>What's confusing?</span>
          </div>
        }
        onCancel={() => { setHelpModalOpen(false); setHelpNote(""); }}
        onOk={() => {
          const currentCard = cards[currentCardIndex];
          if (!currentCard || !deckId || !sessionId) return;
          submitHelpMutation.mutate({
            note: helpNote,
            cardId: currentCard.id,
            deckId,
            sessionId,
          });
        }}
        okText="Send & Continue"
        cancelText="Cancel"
        okButtonProps={{ loading: submitHelpMutation.isPending, style: { background: "#f59e0b", borderColor: "#f59e0b" } }}
        centered
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 14 }}>
          Tell your teacher what part of this card you didn't understand. This is optional but helps them help you better!
        </Text>
        <div style={{ marginBottom: 24 }}>
          <Input.TextArea
            value={helpNote}
            onChange={(e) => setHelpNote(e.target.value)}
            placeholder="e.g. I don't understand what photosynthesis means..."
            rows={3}
            maxLength={300}
            showCount
            autoFocus
          />
        </div>
      </Modal>

      {/* AI Chat modal */}
      <AiChatModal
        key={aiChatKey}
        open={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        cardId={currentCard?.id ?? ''}
        sessionId={sessionId}
        cardTitle={currentCard?.title ?? undefined}
      />

      {/* Exit confirmation modal */}
      <Modal
        open={showExitConfirm}
        title="Exit Review?"
        onCancel={() => setShowExitConfirm(false)}
        onOk={handleExit}
        okText="Yes, exit"
        cancelText="Keep going"
        okButtonProps={{ danger: true }}
        centered
      >
        <Text style={{ fontSize: 15 }}>
          Your progress has been saved. You can resume this deck anytime from
          your home page.
        </Text>
      </Modal>
    </div>
  );
}
